<template>
  <div class="floor-plan">
    <div class="floor-plan__wrapper">
      <div class="floor-plan__content">
        <h1 class="floor-plan__title">{{ title }}</h1>
        <span class="floor-plan__desc">{{ description }}</span>
        <div class="floor-plan__floor">
          <div class="plan-bulletpoints">
            <div
              v-for="(bulletpoint, i) in bulletPoints"
              :key="i"
              class="plan-bulletpoints__item"
            >
              <dotcom-icon
                name="check"
                art="light"
                color="--c-primary-color-2"
                hover-color="--c-primary-color-2"
              />
              <span class="plan-bulletpoints__title"> {{ bulletpoint }} </span>
            </div>
          </div>
          <div class="floor-plan__image">
            <img :src="floorPlanImage" height="265px" width="940px" />
            <template v-for="(point, i) in floorPoints">
              <div
                :key="i"
                class="floor-plan__point"
                :class="{ selected: selectedIndex === i }"
                :style="pointStyle(point)"
                @click="selectedIndex = i"
              ></div>
            </template>
          </div>
        </div>
      </div>
      <div v-if="selectedIndex >= 0" class="floor-plan__overlay">
        <div class="overlay__image">
          <img
            :src="floorPoints[selectedIndex].pointImage"
            height="214px"
            width="100%"
          />
          <dotcom-icon
            class="close-icon"
            name="xmark"
            art="light"
            color="--c-primary-color-3"
            hover-color="--c-tint-color-1"
            @click="selectedIndex = -1"
          />
        </div>
        <div class="overlay__content">
          <div class="top">
            <dotcom-icon
              class="prev-icon"
              name="chevron-right"
              art="regular"
              rotation="180"
              color="--c-primary-color-3"
              hover-color="--c-tint-color-1"
              @click="prev"
            />
            <span class="top__title">{{
              floorPoints[selectedIndex].pointTitle
            }}</span>
            <dotcom-icon
              class="next-icon"
              name="chevron-right"
              art="regular"
              color="--c-primary-color-3"
              hover-color="--c-tint-color-1"
              @click="next"
            />
          </div>
          <div class="center">
            {{ floorPoints[selectedIndex].pointDescription }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SanitasFloorPlan',
  components: {
    DotcomIcon: () => {
      return import(
        /* webpackChunkName: "dotcom-icon" */
        '@/modules/icon/Icon'
      )
    },
  },
  props: {
    floorPlanImage: {
      type: String,
      default: '',
    },
    title: {
      type: String,
      default: '',
    },
    description: {
      type: String,
      default: '',
    },
    bulletPoints: {
      type: Array,
      default: () => [],
    },
    floorPoints: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    selectedIndex: -1,
  }),
  methods: {
    prev() {
      if (this.selectedIndex === 0) {
        this.selectedIndex = this.floorPoints.length - 1
      } else {
        this.selectedIndex--
      }
    },
    next() {
      if (this.selectedIndex === this.floorPoints.length - 1) {
        this.selectedIndex = 0
      } else {
        this.selectedIndex++
      }
    },
    pointStyle({ xposition, yposition }) {
      return {
        left: `${xposition}px`,
        top: `${yposition}px`,
      }
    },
  },
}
</script>

<style scoped>
.floor-plan {
  &__wrapper {
    width: 100%;
    margin: auto;
    max-width: 1000px;
    position: relative;
  }

  &__content {
    display: flex;
    flex-direction: column;
    max-width: 940px;
    margin: 0;
  }

  &__title {
    font-size: 30px;
    font-weight: bold;
    line-height: 35px;
    color: var(--c-primary-neutral-1);
    margin-bottom: 10px;
  }

  &__desc {
    padding-bottom: 30px;
    font-size: 15px;
    font-weight: normal;
    font-style: normal;
    line-height: 1.33;
    color: var(--c-primary-neutral-1);
  }

  &__floor {
    display: flex;
    flex-direction: column;
  }

  &__image {
    overflow-x: auto;
    overflow-y: hidden;
    max-width: 940px;
    position: relative;
  }

  .plan-bulletpoints {
    padding-bottom: 30px;

    &__item {
      padding-bottom: 10px;
      width: 100%;
      display: flex;
      align-items: center;
    }

    &__title {
      margin-left: 16px;
      margin-top: 0;
      margin-right: 0;
      font-size: 15px;
      line-height: 1.33;
      color: var(--c-primary-neutral-1);
      max-width: calc(100% - 36px);
    }
  }

  &__overlay {
    position: absolute;
    right: 0;
    top: 0;
    width: 380px;
    background-color: var(--c-primary-neutral-2);
    display: flex;
    flex-direction: column;

    .close-icon {
      height: 34px;
      width: 34px;
      padding: var(--spacing-2);
      box-sizing: border-box;
    }

    .overlay {
      &__image {
        position: relative;
        height: 214px;

        .dotcom-icon {
          position: absolute;
          right: 0;
          top: 0;
          cursor: pointer;
        }
      }

      &__content {
        height: 257px;

        .top {
          margin: var(--spacing-4) var(--spacing-2) var(--spacing-2);
          display: flex;
          align-items: center;
          min-height: 30px;

          .prev-icon,
          .next-icon {
            cursor: pointer;
            font-size: 15px;
            width: 18px;
          }

          .prev-icon {
            margin-right: var(--spacing-1);
          }

          .next-icon {
            margin-left: var(--spacing-1);
          }

          &__title {
            margin: auto auto auto 0;
            font-size: 15px;
            font-weight: bold;
            line-height: 1.33;
            color: var(--c-primary-neutral-1);
            width: calc(100% - 60px);
          }
        }

        .center {
          padding: 0 25px 20px 30px;
          font-size: 15px;
          line-height: 1.33;
          color: var(--c-primary-neutral-1);
          height: calc(100% - 80px);
          overflow-y: auto;
        }
      }
    }
  }

  &__point {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background-color: var(--c-primary-color-3);
    position: absolute;
    cursor: pointer;

    &:hover {
      background-color: var(--c-primary-color-2);
    }

    &.selected {
      background-color: var(--c-primary-color-2);
    }
  }
}

@media (max-width: 768px) {
  .floor-plan {
    &__title {
      font-size: 23px;
      line-height: 30px;
    }

    &__image {
      padding-bottom: 0;
    }

    &__floor {
      flex-direction: column;
    }

    &__overlay {
      width: 100%;
      position: initial;
      margin-top: 20px;

      &__content {
        .center {
          height: auto;
        }
      }
    }
  }
}
</style>
